/**
 * Parser für die WhatsApp-Textauszeichnung (`*fett*`, `_kursiv_`, `~durchgestrichen~`,
 * ```` ```monospace``` ````).
 *
 * Die KI schickt ihre WhatsApp-Nachrichten mit genau dieser Auszeichnung raus; im
 * Verläufe-Chat sollen sie deshalb so aussehen wie beim Kunden in WhatsApp und nicht
 * als rohe Sternchen.
 *
 * Bewusst ein eigener Parser statt Markdown-Bibliothek + `dangerouslySetInnerHTML`:
 * das Ergebnis ist ein reiner Datenbaum, der als React-Elemente gerendert wird —
 * Nachrichteninhalte sind Fremdeingaben und dürfen nie als HTML interpretiert werden.
 */

export type MarkupNodeType = 'bold' | 'italic' | 'strike' | 'mono';

export type MarkupNode =
  | { type: 'text'; value: string }
  | { type: MarkupNodeType; children: MarkupNode[] };

type MarkerRule = {
  readonly marker: string;
  readonly type: MarkupNodeType;
};

/** Längere Marker zuerst prüfen, sonst schluckt `` ` `` das ```` ``` ````. */
const MARKER_RULES: readonly MarkerRule[] = [
  { marker: '```', type: 'mono' },
  { marker: '*', type: 'bold' },
  { marker: '_', type: 'italic' },
  { marker: '~', type: 'strike' },
];

const WORD_CHAR = /[\p{L}\p{N}]/u;
const WHITESPACE = /\s/;

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && WORD_CHAR.test(char);
}

function isWhitespace(char: string | undefined): boolean {
  return char !== undefined && WHITESPACE.test(char);
}

/**
 * Ein öffnender Marker steht am Wortanfang (Textstart oder nach einem Nicht-Wort-Zeichen)
 * und wird direkt von Inhalt gefolgt — `3 * 4` bleibt so normale Rechnung und
 * `snake_case` bleibt ein Bezeichner.
 */
function isValidOpener(text: string, index: number, rule: MarkerRule): boolean {
  if (!text.startsWith(rule.marker, index)) return false;
  if (isWordChar(text[index - 1])) return false;

  const firstContentChar = text[index + rule.marker.length];
  if (firstContentChar === undefined) return false;
  if (isWhitespace(firstContentChar)) return false;

  return true;
}

/**
 * Ein schließender Marker folgt direkt auf Inhalt und leitet kein neues Wort ein.
 */
function isValidCloser(text: string, index: number, rule: MarkerRule): boolean {
  if (!text.startsWith(rule.marker, index)) return false;
  if (isWhitespace(text[index - 1])) return false;

  return !isWordChar(text[index + rule.marker.length]);
}

/** Position des passenden schließenden Markers, oder -1. */
function findCloser(text: string, contentStart: number, rule: MarkerRule): number {
  for (let i = contentStart; i < text.length; i += 1) {
    if (isValidCloser(text, i, rule)) return i;
  }
  return -1;
}

function pushText(nodes: MarkupNode[], value: string): MarkupNode[] {
  if (!value) return nodes;

  const last = nodes[nodes.length - 1];
  if (last && last.type === 'text') {
    // Zusammenhängenden Text zu einem Knoten verschmelzen, damit die Ausgabe
    // unabhängig davon ist, wo ungültige Marker den Scan unterbrochen haben.
    return [...nodes.slice(0, -1), { type: 'text', value: last.value + value }];
  }
  return [...nodes, { type: 'text', value }];
}

/**
 * Zerlegt WhatsApp-Text in einen Auszeichnungsbaum. Unpaarige oder ungültig
 * platzierte Marker bleiben wörtlicher Text.
 */
export function parseWhatsAppMarkup(text: string): MarkupNode[] {
  let nodes: MarkupNode[] = [];
  let pending = '';
  let cursor = 0;

  while (cursor < text.length) {
    const rule = MARKER_RULES.find((candidate) => isValidOpener(text, cursor, candidate));

    if (rule) {
      const contentStart = cursor + rule.marker.length;
      const closerIndex = findCloser(text, contentStart, rule);

      if (closerIndex > contentStart) {
        nodes = pushText(nodes, pending);
        pending = '';
        nodes = [
          ...nodes,
          {
            type: rule.type,
            children: parseWhatsAppMarkup(text.slice(contentStart, closerIndex)),
          },
        ];
        cursor = closerIndex + rule.marker.length;
        continue;
      }
    }

    pending += text[cursor];
    cursor += 1;
  }

  return pushText(nodes, pending);
}

/** Nur der reine Text — für Vorschauzeilen und Suche, wo keine Formatierung passt. */
export function stripWhatsAppMarkup(text: string | null | undefined): string {
  if (!text) return '';

  const flatten = (nodes: MarkupNode[]): string =>
    nodes.map((node) => (node.type === 'text' ? node.value : flatten(node.children))).join('');

  return flatten(parseWhatsAppMarkup(text));
}
